import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { Inbox } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * O estado vazio, com nome e saída.
 *
 * Substitui os "Vazio", "Nenhum registro encontrado" e afins espalhados pelas
 * telas. A regra é que a `descricao` diga O QUE FAZER para sair daqui — uma
 * tela que só informa que não há nada deixa o usuário sem próximo passo.
 *
 * **Coluna vazia de kanban é estado, não buraco:** ela também ganha este
 * componente, em `compacto`, dizendo o que apareceria ali.
 *
 * Lista vazia de verdade e busca sem resultado são coisas DIFERENTES. Para a
 * segunda existe `NadaEncontrado`, que cita o termo procurado — dizer "nenhum
 * funcionário cadastrado" para quem digitou "jaao" é mentira.
 */
export interface EmptyStateProps {
  titulo: string;
  descricao?: string;
  acao?: React.ReactNode;
  icone?: LucideIcon;
  compacto?: boolean;
  className?: string;
  testId?: string;
}

export function EmptyState({
  titulo,
  descricao,
  acao,
  icone: Icone = Inbox,
  compacto,
  className,
  testId,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center gap-2",
        compacto ? "py-6 px-3" : "py-12 px-4",
        className,
      )}
      data-testid={testId}
    >
      <Icone className={cn("text-muted-foreground", compacto ? "h-5 w-5" : "h-8 w-8")} />
      <p className={cn("font-medium", compacto ? "text-xs" : "text-sm")}>{titulo}</p>
      {descricao && (
        <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">{descricao}</p>
      )}
      {acao && <div className="mt-1">{acao}</div>}
    </div>
  );
}

/** Busca sem resultado — não é a mesma coisa que lista vazia. */
export function NadaEncontrado({
  termo,
  compacto,
  testId = "empty-nada-encontrado",
}: {
  termo: string;
  compacto?: boolean;
  testId?: string;
}) {
  return (
    <EmptyState
      titulo="Nada encontrado"
      descricao={`Nenhum resultado para “${termo}”. Confira a grafia ou limpe a busca.`}
      compacto={compacto}
      testId={testId}
    />
  );
}
